import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, TestTube2, Package, BadgeCheck, Home } from 'lucide-react';
import { patientApi } from '../api';

export default function PatientLabDiscovery() {
  const navigate = useNavigate();
  const [labs, setLabs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [homeCollection, setHomeCollection] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (homeCollection) params.homeCollection = 'true';
    patientApi.lab.searchLabs(params)
      .then((data: any) => { if (alive) setLabs(Array.isArray(data) ? data : data?.labs || []); })
      .catch(() => { if (alive) setLabs([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [search, homeCollection]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Find Labs</h1>
        <p className="text-sm text-gray-500 mt-1">Search diagnostic labs</p>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by city, lab name, or test..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => setHomeCollection(!homeCollection)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
            homeCollection ? 'bg-teal-50 border-teal-300 text-teal-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Home className="w-4 h-4" />
          Home Collection
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-5 animate-pulse">
              <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" />
              <div className="h-4 bg-gray-100 rounded w-1/2 mb-2" />
              <div className="h-4 bg-gray-100 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : labs.length === 0 ? (
        <div className="text-center py-16">
          <TestTube2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No labs found</p>
          <p className="text-sm text-gray-400 mt-1">Try a different search or location</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {labs.map((lab: any) => (
            <button
              key={lab.id || lab._id}
              onClick={() => navigate(`/patient/labs/${lab.labProfileId || lab.id || lab._id}`)}
              className="bg-white rounded-xl border border-gray-200 p-5 text-left hover:shadow-md hover:border-teal-200 transition-all group"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <h3 className="text-sm font-bold text-gray-900 group-hover:text-teal-700 transition-colors">
                  {lab.labName || lab.name}
                </h3>
                {lab.accreditation && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 shrink-0">
                    <BadgeCheck className="w-3 h-3" />
                    {lab.accreditation}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                <MapPin className="w-3.5 h-3.5" />
                {lab.city || lab.address?.city || 'N/A'}
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <TestTube2 className="w-3.5 h-3.5 text-teal-500" />
                  {lab.testCount ?? '—'} tests
                </span>
                <span className="flex items-center gap-1">
                  <Package className="w-3.5 h-3.5 text-teal-500" />
                  {lab.packageCount ?? '—'} packages
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
